/* eslint-disable react/jsx-curly-newline */
/* eslint-disable no-param-reassign */
import { cloneDeep } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import { BetType, Race } from '@/lib/apollo/types/gen/graphql';
import { getBetMethodName, getBetTypeName, isPossibleMulti } from '@/lib/race';
import {
  BetData,
  BetDataList,
  BetDetail,
  BetMethod,
  CompositeOddsDetail,
  OddsData,
  OrderStep,
} from '@/modelTypes';

import DeleteAllButton from '../Partials/DeleteAllButton';
import ModifyButton from '../Partials/ModifyButton';
import { EmptyText } from '../style';
import CollapseList from './CollapseList';
import ExpandList from './ExpandList';
import FundSetting from './FundSetting';
import {
  Detail,
  DetailWrapper,
  FlexWrapper,
  FundSettingArea,
  FundWrapper,
  TicketCount,
  TicketInfo,
  TicketTypeWrapper,
} from './style';
import TicketHeader from './TicketHeader';

interface TicketBoxProps {
  race: Race;
  velodromeCode: number;
  oddsData: OddsData;
  betData: BetData;
  status: OrderStep;
  setBetDataList: React.Dispatch<React.SetStateAction<BetDataList>>;
  modifyTheBetDetail: (betDetail: BetDetail, key: string) => void;
  modifyBetDetailKey: string;
  openDeleteAllBetModal: () => void;
  compositeOddsDetail: CompositeOddsDetail;
}

const TicketBox: React.FC<TicketBoxProps> = ({
  race,
  velodromeCode,
  oddsData,
  betData,
  status,
  setBetDataList,
  modifyTheBetDetail,
  modifyBetDetailKey,
  openDeleteAllBetModal,
  compositeOddsDetail,
}) => {
  const [openList, setOpenList] = useState<string[]>([]);
  const isReady = status === OrderStep.Ready;

  // 各チケット口数及び削除フラグが同一出ない場合は買い目が展開された状態にする
  useEffect(() => {
    Object.keys(betData).forEach(key => {
      const arrayOfCount = Array.from(
        new Set(betData[key].tickets.map(t => t.count)),
      );
      const arrayOfIsRemoved = Array.from(
        new Set(betData[key].tickets.map(t => t.isRemoved)),
      );
      if (
        arrayOfCount.length !== 1 ||
        arrayOfIsRemoved.length !== 1 ||
        betData[key].betMethod === BetMethod.Odds
      ) {
        setOpenList(prev => [...prev, key]);
      }
    });
  }, []);

  const changeCount = (value: number, itemId: string, setNum?: number) => {
    setBetDataList(prev => {
      const temp = { ...prev };
      temp[race.id][itemId].tickets.forEach(t => {
        if (!setNum || t.setNum === setNum) {
          t.count = value;
        }
      });
      return temp;
    });
  };

  const deleteTicket = (itemId: string, setNum?: number) =>
    setBetDataList(prev => {
      const temp = { ...prev };
      temp[race.id][itemId].tickets.forEach(t => {
        if (!setNum || t.setNum === setNum) {
          t.isRemoved = !t.isRemoved;
        }
      });
      return temp;
    });

  const getValidTicketCount = (tickets: BetDetail['tickets']) => {
    return tickets.filter(t => !t.isRemoved).length;
  };

  const TicketType: React.FC<{
    betType: BetType;
    betMethod: BetMethod;
    isMulti: boolean;
  }> = ({ betType, betMethod, isMulti }) => {
    const typeName = useMemo(
      () =>
        `${getBetTypeName(betType)} ${getBetMethodName(betMethod)} ${
          isMulti ? 'マルチ' : ''
        }`,
      [betType, betMethod],
    );
    return <p>{typeName}</p>;
  };

  const betDetails = useMemo(() => {
    return Object.values(betData).map(bd => bd);
  }, [betData]);

  return (
    <>
      {Object.keys(betData).length ? (
        <>
          <TicketHeader
            isReady={isReady}
            velodromeCode={velodromeCode}
            race={race}
          />
          {isReady && oddsData.exists && (
            <FundSettingArea>
              <DetailWrapper>
                <Detail>
                  <p>オッズ</p>
                  <span>
                    {compositeOddsDetail.min.toFixed(1)}
                    <span>~</span>
                    {compositeOddsDetail.max.toFixed(1)}
                  </span>
                </Detail>
                <Detail>
                  <p>合成オッズ</p>
                  <span>{compositeOddsDetail.composite.toFixed(1)}</span>
                </Detail>
                <Detail>
                  <p>想定払戻金額</p>
                  <span>
                    {compositeOddsDetail.minRefund.toLocaleString()}円
                    <span>~</span>
                    {compositeOddsDetail.maxRefund.toLocaleString()}円
                  </span>
                </Detail>
              </DetailWrapper>
              <FundSetting
                raceId={race.id}
                odds={oddsData.data}
                betDetails={betDetails}
                setBetDataList={setBetDataList}
                setOpenList={setOpenList}
              />
            </FundSettingArea>
          )}
          {Object.keys(betData)
            .reverse()
            .map(key => (
              <React.Fragment key={key}>
                {Boolean(betData[key].tickets.length) && (
                  <TicketInfo>
                    <FlexWrapper
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <TicketTypeWrapper>
                        <TicketType
                          betType={betData[key].betType}
                          betMethod={betData[key].betMethod}
                          isMulti={
                            isPossibleMulti(betData[key].betType) &&
                            betData[key].isMulti
                          }
                        />
                        <TicketCount>
                          {getValidTicketCount(betData[key].tickets)}組
                        </TicketCount>
                      </TicketTypeWrapper>
                      {isReady && (
                        <ModifyButton
                          isModified={modifyBetDetailKey === key}
                          modifyFn={() =>
                            modifyTheBetDetail(cloneDeep(betData[key]), key)
                          }
                        />
                      )}
                    </FlexWrapper>
                    <FundWrapper>
                      {isReady && oddsData.exists && (
                        <FundSetting
                          raceId={race.id}
                          itemId={key}
                          odds={oddsData.data}
                          betDetails={[betData[key]]}
                          setBetDataList={setBetDataList}
                          setOpenList={setOpenList}
                        />
                      )}
                    </FundWrapper>
                    {openList.includes(key) ? (
                      <ExpandList
                        betDetail={betData[key]}
                        orderStep={status}
                        oddsData={oddsData}
                        hashKey={key}
                        changeCount={changeCount}
                        deleteTicket={deleteTicket}
                      />
                    ) : (
                      <CollapseList
                        betDetail={betData[key]}
                        orderStep={status}
                        oddsData={oddsData}
                        hashKey={key}
                        changeCount={changeCount}
                        deleteTicket={deleteTicket}
                        setOpenList={setOpenList}
                      />
                    )}
                  </TicketInfo>
                )}
              </React.Fragment>
            ))}
          {isReady && <DeleteAllButton openModal={openDeleteAllBetModal} />}
        </>
      ) : (
        <EmptyText>買い目はありません</EmptyText>
      )}
    </>
  );
};

export default TicketBox;
